import { useMemo, useState } from 'react';
import {
  AlertCircle,
  TrendingDown,
  AlertTriangle,
  Activity,
  TrendingUp,
  CheckCircle,
  RefreshCw,
  Building2,
  ChevronDown,
  ArrowRight,
  Boxes,
  Clock3,
  Gauge,
  Layers3,
  Target,
  Truck,
  Warehouse,
} from 'lucide-react';
import { useProductsGrouped } from '../shared/hooks/useProducts';
import { useSettings } from '../shared/hooks/useSettings';
import { ProductWithCalculations, StatusType } from '../shared/types/inventory';
import { HorizontalBarChart } from '../components/charts/HorizontalBarChart';
import { CoverageGauge } from '../components/charts/CoverageGauge';
import { MiniSparkBar } from '../components/charts/MiniSparkBar';
import { InventoryAnalysisModal, type InventoryAnalysisItem } from '../components/InventoryAnalysisModal';

import { SellerPageLoader } from '../shared/components/SellerPageLoader';
import { CardHelp } from '../shared/components/CardHelp';
const STATUS_PALETTE: Record<StatusType, string> = {
  ruptura: '#dc2626',
  iminente: '#ea580c',
  minimo: '#ca8a04',
  critica: '#0891b2',
  excesso: '#2563eb',
  saudavel: '#16a34a',
};

const STATUS_META: Record<StatusType, { label: string; icon: typeof AlertCircle; bgClass: string; textClass: string }> = {
  ruptura: { label: 'Ruptura Instalada', icon: AlertCircle, bgClass: 'bg-red-50 border-red-100', textClass: 'text-red-700' },
  iminente: { label: 'Ruptura Iminente', icon: TrendingDown, bgClass: 'bg-orange-50 border-orange-100', textClass: 'text-orange-700' },
  minimo: { label: 'Perto do Mínimo', icon: AlertTriangle, bgClass: 'bg-yellow-50 border-yellow-100', textClass: 'text-yellow-700' },
  critica: { label: 'Cobertura Crítica', icon: Activity, bgClass: 'bg-cyan-50 border-cyan-100', textClass: 'text-cyan-700' },
  excesso: { label: 'Excesso', icon: TrendingUp, bgClass: 'bg-blue-50 border-blue-100', textClass: 'text-blue-700' },
  saudavel: { label: 'Saudável', icon: CheckCircle, bgClass: 'bg-green-50 border-green-100', textClass: 'text-green-700' },
};

const STATUS_ORDER: StatusType[] = ['ruptura', 'iminente', 'minimo', 'critica', 'excesso', 'saudavel'];

function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function computeAnalytics(products: ProductWithCalculations[], diasCoberturaAlvo: number) {
  const counts: Record<StatusType, number> = {
    ruptura: 0, iminente: 0, minimo: 0, critica: 0, excesso: 0, saudavel: 0,
  };
  const coverageBuckets = [
    {key: 'no_consumption', label: 'Sem consumo', description: 'CMD igual a zero', value: 0, color: '#94a3b8'},
    {key: 'stockout', label: 'Sem estoque', description: 'Com consumo e estoque zerado', value: 0, color: '#dc2626'},
    {key: 'below_lead', label: 'Abaixo do lead time', description: 'Risco antes da próxima entrega', value: 0, color: '#ea580c'},
    {key: 'below_target', label: 'Abaixo da meta', description: 'Entre o lead time e a cobertura alvo', value: 0, color: '#ca8a04'},
    {key: 'on_target', label: 'Cobertura adequada', description: 'Dentro da faixa planejada', value: 0, color: '#16a34a'},
    {key: 'high', label: 'Cobertura elevada', description: 'Acima de 150% da meta', value: 0, color: '#2563eb'},
  ];
  const suppliers = new Map<string, {
    name: string; products: number; alerts: number; suggested: number;
    stock: number; coverageSum: number; coverageCount: number;
  }>();
  const coverageValues: number[] = [];
  let totalStock = 0;
  let totalTarget = 0;
  let totalSuggested = 0;
  let coverageSum = 0;
  let coverageCount = 0;
  let excessUnits = 0;
  let zeroStockCount = 0;
  let noSalesCount = 0;
  let belowLeadTimeCount = 0;

  for (const product of products) {
    counts[product.status]++;
    totalStock += product.estoqueAtual;
    totalTarget += product.estoqueAlvo;
    totalSuggested += product.pedidoSugerido;
    excessUnits += Math.max(0, product.estoqueAtual - product.estoqueAlvo);
    if (product.estoqueAtual <= 0) zeroStockCount++;
    if (product.cmd <= 0) noSalesCount++;

    const leadTime = product.leadTime ?? 0;
    if (product.cmd > 0) {
      coverageSum += product.cobertura;
      coverageCount++;
      coverageValues.push(product.cobertura);
      if (product.cobertura < leadTime) belowLeadTimeCount++;
    }

    if (product.cmd <= 0) coverageBuckets[0].value++;
    else if (product.estoqueAtual <= 0) coverageBuckets[1].value++;
    else if (product.cobertura < leadTime) coverageBuckets[2].value++;
    else if (product.cobertura < diasCoberturaAlvo) coverageBuckets[3].value++;
    else if (product.cobertura <= diasCoberturaAlvo * 1.5) coverageBuckets[4].value++;
    else coverageBuckets[5].value++;

    const supplierName = product.fornecedor?.trim() || 'Sem fornecedor';
    const supplier = suppliers.get(supplierName) || {
      name: supplierName, products: 0, alerts: 0, suggested: 0,
      stock: 0, coverageSum: 0, coverageCount: 0,
    };
    supplier.products++;
    supplier.stock += product.estoqueAtual;
    supplier.suggested += product.pedidoSugerido;
    if (product.status === 'ruptura' || product.status === 'iminente' || product.status === 'minimo') supplier.alerts++;
    if (product.cmd > 0) {
      supplier.coverageSum += product.cobertura;
      supplier.coverageCount++;
    }
    suppliers.set(supplierName, supplier);
  }

  coverageValues.sort((a, b) => a - b);
  const medianCoverage = coverageValues.length
    ? coverageValues.length % 2
      ? coverageValues[Math.floor(coverageValues.length / 2)]
      : (coverageValues[coverageValues.length / 2 - 1] + coverageValues[coverageValues.length / 2]) / 2
    : 0;
  const avgCoverage = coverageCount > 0 ? coverageSum / coverageCount : 0;
  const alertCount = counts.ruptura + counts.iminente + counts.minimo;
  const healthPct = products.length > 0 ? ((counts.saudavel / products.length) * 100) : 0;
  const fillRate = totalTarget > 0 ? (totalStock / totalTarget) * 100 : 0;
  const riskPoints = counts.ruptura + counts.iminente * 0.8 + counts.minimo * 0.55
    + counts.critica * 0.35 + counts.excesso * 0.2;
  const operationalScore = products.length
    ? Math.max(0, Math.round(100 - (riskPoints / products.length) * 100))
    : 0;

  const topLowCoverage = [...products]
    .filter((product) => product.cmd > 0)
    .sort((a, b) => a.cobertura - b.cobertura)
    .slice(0, 8);
  const topHighOrder = [...products]
    .filter((product) => product.pedidoSugerido > 0)
    .sort((a, b) => b.pedidoSugerido - a.pedidoSugerido)
    .slice(0, 8);
  const supplierInsights = Array.from(suppliers.values())
    .map((supplier) => ({
      ...supplier,
      alertPct: supplier.products ? (supplier.alerts / supplier.products) * 100 : 0,
      avgCoverage: supplier.coverageCount ? supplier.coverageSum / supplier.coverageCount : 0,
    }))
    .sort((a, b) => b.alerts - a.alerts || b.suggested - a.suggested)
    .slice(0, 8);

  return {
    counts, totalStock, totalTarget, totalSuggested, avgCoverage, medianCoverage,
    alertCount, healthPct, fillRate, operationalScore, excessUnits, zeroStockCount,
    noSalesCount, belowLeadTimeCount, stockGap: Math.max(0, totalTarget - totalStock),
    coverageBuckets, supplierInsights, topLowCoverage, topHighOrder,
  };
}

type DashboardAnalytics = ReturnType<typeof computeAnalytics>;
type CnpjPerformance = {
  cnpj: string;
  razaoSocial: string;
  products: number;
  alerts: number;
  alertPct: number;
  avgCoverage: number;
  suggested: number;
  score: number;
};

export const DashboardPage = () => {
  const { settings } = useSettings();
  const { groups, allProducts, loading, error, refetch } = useProductsGrouped();
  const [selectedCNPJ, setSelectedCNPJ] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [analysisMode, setAnalysisMode] = useState<'coverage' | 'orders' | null>(null);

  const hasMultipleCNPJs = groups.length > 1;


  const activeProducts = useMemo(() => {
    if (!selectedCNPJ) return allProducts;
    const group = groups.find((g) => g.cnpj === selectedCNPJ);
    return group ? group.products : allProducts;
  }, [groups, allProducts, selectedCNPJ]);

  const analysisProducts = useMemo<InventoryAnalysisItem[]>(() => {
    if (selectedCNPJ) {
      const group = groups.find((item) => item.cnpj === selectedCNPJ);
      return group ? group.products.map((product) => ({ ...product, cnpj: group.cnpj, razaoSocial: group.razaoSocial || '' })) : [];
    }
    return groups.flatMap((group) => group.products.map((product) => ({ ...product, cnpj: group.cnpj, razaoSocial: group.razaoSocial || '' })));
  }, [groups, selectedCNPJ]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.cnpj === selectedCNPJ),
    [groups, selectedCNPJ]
  );

  const analytics = useMemo(
    () => computeAnalytics(activeProducts, settings.diasCoberturaAlvo),
    [activeProducts, settings.diasCoberturaAlvo]
  );
  const averageLeadTime = useMemo(() => {
    if (!activeProducts.length) return settings.leadTime;
    return activeProducts.reduce(
      (sum, product) => sum + (product.leadTime ?? settings.leadTime),
      0
    ) / activeProducts.length;
  }, [activeProducts, settings.leadTime]);
  const cnpjPerformance = useMemo<CnpjPerformance[]>(() => groups.map((group) => {
    const performance = computeAnalytics(group.products, settings.diasCoberturaAlvo)
    return {
      cnpj: group.cnpj,
      razaoSocial: group.razaoSocial || '',
      products: group.products.length,
      alerts: performance.alertCount,
      alertPct: group.products.length ? (performance.alertCount / group.products.length) * 100 : 0,
      avgCoverage: performance.avgCoverage,
      suggested: performance.totalSuggested,
      score: performance.operationalScore,
    }
  }).sort((a, b) => b.alertPct - a.alertPct), [groups, settings.diasCoberturaAlvo])

  const executiveHeadline = analytics.counts.ruptura > 0
    ? `${analytics.counts.ruptura} produtos estão sem estoque e exigem ação imediata.`
    : analytics.belowLeadTimeCount > 0
      ? `${analytics.belowLeadTimeCount} produtos possuem cobertura menor que o prazo de entrega.`
      : analytics.alertCount > 0
        ? `${analytics.alertCount} produtos precisam de acompanhamento para evitar ruptura.`
        : 'O estoque monitorado está operando sem alertas urgentes.'

  if (loading) {
    return (
      <SellerPageLoader
        title={'Montando a vis\u00e3o geral'}
        description="Consolidando indicadores, alertas e cobertura do estoque."
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-gray-900 font-semibold mb-1">Erro ao carregar dados</p>
            <p className="text-gray-500 text-sm">{error}</p>
          </div>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const sparkValues = STATUS_ORDER.map((s) => ({
    label: STATUS_META[s].label,
    value: analytics.counts[s],
    color: STATUS_PALETTE[s],
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Visão Geral do Estoque</h1>
          <p className="text-gray-500 text-sm">
            {activeProducts.length} produtos monitorados &middot; Lead time médio {averageLeadTime.toFixed(1)}d &middot; Cobertura alvo {settings.diasCoberturaAlvo}d
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {hasMultipleCNPJs && (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm min-w-[260px]"
              >
                <Building2 className="w-4 h-4 text-teal-600 flex-shrink-0" />
                <span className="truncate">
                  {selectedCNPJ ? formatCNPJ(selectedCNPJ) : 'Todos os CNPJs'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setDropdownOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-40 w-full min-w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCNPJ(null);
                        setDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left transition-colors ${selectedCNPJ === null ? 'bg-teal-50 text-teal-900' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <span className="flex items-center gap-3">
                        <Building2 className={`h-4 w-4 ${selectedCNPJ === null ? 'text-teal-600' : 'text-gray-400'}`} />
                        <span>
                          <strong className="block text-sm">Todos os CNPJs</strong>
                          <span className="text-xs text-gray-500">{allProducts.length} produtos monitorados</span>
                        </span>
                      </span>
                    </button>
                    {groups.map((group) => {
                      const isSelected = group.cnpj === selectedCNPJ;
                      return (
                        <button
                          key={group.cnpj}
                          onClick={() => {
                            setSelectedCNPJ(group.cnpj);
                            setDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                            isSelected
                              ? 'bg-teal-50 text-teal-900'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
                          <div className="min-w-0">
                            <p className={`text-sm font-mono font-medium truncate ${isSelected ? 'text-teal-900' : 'text-gray-900'}`}>
                              {formatCNPJ(group.cnpj)}
                            </p>
                            {group.razaoSocial && (
                              <p className="text-xs text-gray-500 truncate">{group.razaoSocial}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">
                              {group.products.length} {group.products.length === 1 ? 'produto' : 'produtos'}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="ml-auto flex-shrink-0 w-2 h-2 rounded-full bg-teal-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {hasMultipleCNPJs && selectedGroup && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 border border-teal-100 rounded-lg">
          <Building2 className="w-4 h-4 text-teal-600" />
          <span className="text-sm text-teal-800">
            Exibindo dados de <strong className="font-mono">{formatCNPJ(selectedGroup.cnpj)}</strong>
            {selectedGroup.razaoSocial && <span className="text-teal-600"> - {selectedGroup.razaoSocial}</span>}
          </span>
        </div>
      )}

      <ExecutiveSummary
        analytics={analytics}
        headline={executiveHeadline}
        averageLeadTime={averageLeadTime}
        targetCoverage={settings.diasCoberturaAlvo}
        scopeLabel={selectedCNPJ ? formatCNPJ(selectedCNPJ) : `${groups.length} CNPJs consolidados`}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <CardHelp content="Ordena os produtos pelos menores dias de cobertura. Cobertura = estoque atual dividido pelo consumo médio diário." className="absolute right-3 top-3" />
          <button type="button" onClick={() => setAnalysisMode('coverage')} className="mb-1 flex w-full items-center justify-between pr-7 text-left">
            <h3 className="text-sm font-semibold text-gray-700">Menor Cobertura (dias)</h3>
            <span className="text-xs font-semibold text-teal-600 hover:text-teal-700">Ver análise completa</span>
          </button>
          <p className="text-xs text-gray-400 mb-4">Produtos com menos dias de cobertura</p>
          {analytics.topLowCoverage.length > 0 ? (
            <HorizontalBarChart
              items={analytics.topLowCoverage.map((p) => ({
                label: p.nome,
                image: p.foto,
                ean: p.ean,
                details: {
                  status: p.status, statusLabel: p.statusLabel, estoqueAtual: p.estoqueAtual,
                  cmd: p.cmd, cobertura: p.cobertura, leadTime: p.leadTime,
                  estoqueAlvo: p.estoqueAlvo, pedidoSugerido: p.pedidoSugerido, fornecedor: p.fornecedor,
                },
                value: p.cobertura,
                color: p.cobertura < (p.leadTime ?? settings.leadTime)
                  ? '#dc2626'
                  : p.cobertura < settings.diasCoberturaAlvo
                    ? '#ca8a04'
                    : '#16a34a',
              }))}
              formatValue={(v) => `${v.toFixed(1)}d`}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Sem dados de cobertura disponíveis</p>
          )}
        </div>

        <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <CardHelp content="Lista os produtos com maior necessidade de reposição. Pedido sugerido = estoque alvo menos estoque atual, nunca menor que zero." className="absolute right-3 top-3" />
          <button type="button" onClick={() => setAnalysisMode('orders')} className="mb-1 flex w-full items-center justify-between pr-7 text-left">
            <h3 className="text-sm font-semibold text-gray-700">Maiores Pedidos Sugeridos</h3>
            <span className="text-xs font-semibold text-teal-600 hover:text-teal-700">Ver análise completa</span>
          </button>
          <p className="text-xs text-gray-400 mb-4">Produtos que mais precisam de reposição</p>
          {analytics.topHighOrder.length > 0 ? (
            <HorizontalBarChart
              items={analytics.topHighOrder.map((p) => ({
                label: p.nome,
                image: p.foto,
                ean: p.ean,
                details: {
                  status: p.status, statusLabel: p.statusLabel, estoqueAtual: p.estoqueAtual,
                  cmd: p.cmd, cobertura: p.cobertura, leadTime: p.leadTime,
                  estoqueAlvo: p.estoqueAlvo, pedidoSugerido: p.pedidoSugerido, fornecedor: p.fornecedor,
                },
                value: p.pedidoSugerido,
                color: STATUS_PALETTE[p.status],
              }))}
              formatValue={(v) => v.toLocaleString('pt-BR') + ' un'}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum pedido sugerido</p>
          )}
        </div>
      </div>
      <section>
        <div className="mb-3 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-600">{'Ações prioritárias'}</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900">Onde atuar primeiro</h2>
          </div>
          <p className="hidden text-xs text-slate-400 sm:block">Indicadores calculados sobre o filtro atual</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <ActionInsightCard title="Rupturas instaladas" value={analytics.counts.ruptura} description="Produtos sem estoque com demanda registrada" help="Conta produtos com estoque atual igual a zero e consumo registrado no período." icon={<AlertCircle className="h-5 w-5" />} tone="red" onClick={() => setAnalysisMode('coverage')} />
          <ActionInsightCard title="Abaixo do lead time" value={analytics.belowLeadTimeCount} description="Cobertura menor que o prazo do fornecedor" help="Conta produtos em que cobertura (estoque ÷ consumo médio diário) é menor que o prazo de entrega." icon={<Clock3 className="h-5 w-5" />} tone="orange" onClick={() => setAnalysisMode('coverage')} />
          <ActionInsightCard title="Déficit para o estoque alvo" value={analytics.stockGap} description="Unidades necessárias para atingir o nível planejado" help="Soma, por produto, a diferença positiva entre estoque alvo e estoque atual." icon={<Target className="h-5 w-5" />} tone="teal" onClick={() => setAnalysisMode('orders')} suffix=" un" />
          <ActionInsightCard title="Unidades em excesso" value={analytics.excessUnits} description="Estoque acima do alvo calculado por produto" help="Soma, por produto, apenas o estoque atual que ultrapassa o estoque alvo." icon={<Layers3 className="h-5 w-5" />} tone="blue" suffix=" un" />
        </div>
      </section>
      <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <CardHelp content="Mostra a proporção de produtos em cada status de estoque dentro do filtro atual." className="absolute right-3 top-3" />
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Distribuição por status</h3>
        <MiniSparkBar values={sparkValues} />
      </div>

      <div className="grid gap-6 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <CoverageDistribution buckets={analytics.coverageBuckets} total={activeProducts.length} targetCoverage={settings.diasCoberturaAlvo} />
        </div>
        <div className="xl:col-span-2">
          <SupplierRiskPanel suppliers={analytics.supplierInsights} />
        </div>
      </div>
      <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-5">Indicadores de Cobertura</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <CoverageGauge
            label="Cobertura Média"
            value={analytics.avgCoverage}
            max={settings.diasCoberturaAlvo * 2}
            target={settings.diasCoberturaAlvo}
            color={analytics.avgCoverage >= settings.diasCoberturaAlvo ? '#16a34a' : '#ea580c'}
            unit=" dias"
            help="Média de estoque atual dividido pelo consumo médio diário dos produtos com consumo."
          />
          <CoverageGauge
            label="Taxa de Preenchimento"
            value={analytics.fillRate}
            max={120}
            target={100}
            color={analytics.fillRate >= 80 ? '#16a34a' : analytics.fillRate >= 50 ? '#ca8a04' : '#dc2626'}
            unit="%"
            help="Taxa de preenchimento = estoque atual total dividido pelo estoque alvo total, multiplicado por 100."
          />
          <CoverageGauge
            label="Lead Time Médio"
            value={averageLeadTime}
            max={30}
            target={averageLeadTime}
            color="#0891b2"
            unit=" dias"
            help="Média dos prazos de entrega dos fornecedores vinculados aos produtos."
          />
        </div>
      </div>

      {!selectedCNPJ && cnpjPerformance.length > 1 && (
        <CnpjComparison
          rows={cnpjPerformance}
          onSelect={(cnpj) => setSelectedCNPJ(cnpj)}
        />
      )}
      {analysisMode && (
        <InventoryAnalysisModal
          mode={analysisMode}
          products={analysisProducts}
          targetCoverage={settings.diasCoberturaAlvo}
          onClose={() => setAnalysisMode(null)}
        />
      )}
    </div>
  );
};

function ExecutiveSummary({analytics, headline, averageLeadTime, targetCoverage, scopeLabel}: {
  analytics: DashboardAnalytics;
  headline: string;
  averageLeadTime: number;
  targetCoverage: number;
  scopeLabel: string;
}) {
  const scoreColor = analytics.operationalScore >= 75 ? '#059669' : analytics.operationalScore >= 50 ? '#d97706' : '#dc2626';
  const scoreLabel = analytics.operationalScore >= 75 ? 'Operação equilibrada' : analytics.operationalScore >= 50 ? 'Atenção necessária' : 'Risco operacional alto';

  return (
    <section className="relative overflow-hidden rounded-2xl border border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-blue-50 p-6 text-slate-900 shadow-sm sm:p-8">
      <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-teal-300/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-blue-300/20 blur-3xl" />
      <div className="relative grid gap-8 xl:grid-cols-[1fr_auto] xl:items-center">
        <div>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1.5 text-xs font-semibold text-teal-700 shadow-sm">
              <Warehouse className="h-3.5 w-3.5" /> {scopeLabel}
            </span>
            <span className="text-xs text-slate-500">Cobertura alvo: {targetCoverage} dias</span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600">Resumo executivo</p>
          <h2 className="mt-2 max-w-3xl text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">{headline}</h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            O índice combina ruptura, risco de abastecimento, proximidade do mínimo e excesso para priorizar decisões.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <ExecutiveMetric icon={<Boxes className="h-4 w-4" />} label="Estoque atual" value={analytics.totalStock.toLocaleString('pt-BR')} help="Soma do estoque atual de todos os produtos no filtro selecionado." />
            <ExecutiveMetric icon={<Target className="h-4 w-4" />} label="Estoque alvo" value={analytics.totalTarget.toLocaleString('pt-BR')} help="Soma do estoque planejado de todos os produtos no filtro selecionado." />
            <ExecutiveMetric icon={<Gauge className="h-4 w-4" />} label="Cobertura mediana" value={`${analytics.medianCoverage.toFixed(1)}d`} help="Valor central das coberturas. Cobertura = estoque atual ÷ consumo médio diário." />
            <ExecutiveMetric icon={<Truck className="h-4 w-4" />} label="Lead time médio" value={`${averageLeadTime.toFixed(1)}d`} help="Média dos prazos de entrega dos fornecedores associados aos produtos." />
          </div>
        </div>
        <div className="relative flex flex-col items-center rounded-2xl border border-teal-100 bg-white/90 p-6 shadow-sm backdrop-blur-sm">
          <CardHelp content="Índice de 0 a 100 que perde pontos conforme aumentam rupturas, riscos, itens próximos do mínimo e excessos." className="absolute right-3 top-3" />
          <div className="relative flex h-40 w-40 items-center justify-center rounded-full" style={{background: `conic-gradient(${scoreColor} ${analytics.operationalScore * 3.6}deg, #dbeafe 0deg)`}}>
            <div className="flex h-[126px] w-[126px] flex-col items-center justify-center rounded-full border border-slate-100 bg-white shadow-inner">
              <span className="text-4xl font-black text-slate-950">{analytics.operationalScore}</span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">de 100</span>
            </div>
          </div>
          <p className="mt-4 text-sm font-bold" style={{color: scoreColor}}>{scoreLabel}</p>
          <p className="mt-1 text-center text-xs text-slate-500">{analytics.alertCount} produtos em alerta</p>
        </div>
      </div>
    </section>
  );
}

function ExecutiveMetric({icon, label, value, help}: {icon: React.ReactNode; label: string; value: string; help: string}) {
  return (
    <div className="relative rounded-xl border border-slate-200 bg-white/90 p-3 pr-9 shadow-sm">
      <CardHelp content={help} className="absolute right-2 top-2" />
      <div className="flex items-center gap-2 text-teal-600">{icon}<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span></div>
      <p className="mt-2 text-lg font-bold text-slate-950">{value}</p>
    </div>
  );
}

function ActionInsightCard({title, value, description, help, icon, tone, suffix = '', onClick}: {
  title: string;
  value: number;
  description: string;
  help: string;
  icon: React.ReactNode;
  tone: 'red' | 'orange' | 'teal' | 'blue';
  suffix?: string;
  onClick?: () => void;
}) {
  const tones = {
    red: 'border-red-100 bg-red-50 text-red-700',
    orange: 'border-orange-100 bg-orange-50 text-orange-700',
    teal: 'border-teal-100 bg-teal-50 text-teal-700',
    blue: 'border-blue-100 bg-blue-50 text-blue-700',
  };
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className={`rounded-xl border p-2.5 ${tones[tone]}`}>{icon}</span>
        {onClick && <ArrowRight className="mr-7 h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-teal-600" />}
      </div>
      <p className="mt-4 text-3xl font-black tabular-nums text-slate-950">{value.toLocaleString('pt-BR')}{suffix}</p>
      <h3 className="mt-1 text-sm font-bold text-slate-800">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
    </>
  );
  return (
    <div className="relative h-full">
      {onClick ? (
        <button type="button" onClick={onClick} className="group h-full w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-md">{content}</button>
      ) : (
        <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">{content}</div>
      )}
      <CardHelp content={help} className="absolute right-3 top-3" />
    </div>
  );
}
function CoverageDistribution({buckets, total, targetCoverage}: {
  buckets: DashboardAnalytics['coverageBuckets'];
  total: number;
  targetCoverage: number;
}) {
  const maxValue = Math.max(...buckets.map((bucket) => bucket.value), 1);
  return (
    <section className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHelp content="Agrupa os produtos por faixas de cobertura. Cobertura = estoque atual dividido pelo consumo médio diário; as faixas usam lead time e meta configurada." className="absolute right-3 top-3" />
      <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-600">Distribuição de cobertura</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">Faixas de abastecimento</h3>
          <p className="mt-1 text-xs text-slate-500">Meta configurada em {targetCoverage} dias</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">{total} produtos</span>
      </div>
      <div className="mt-6 flex h-4 overflow-hidden rounded-full bg-slate-100">
        {buckets.map((bucket) => (
          <div
            key={bucket.key}
            title={`${bucket.label}: ${bucket.value}`}
            className="h-full transition-all first:rounded-l-full last:rounded-r-full"
            style={{width: `${total ? (bucket.value / total) * 100 : 0}%`, backgroundColor: bucket.color}}
          />
        ))}
      </div>
      <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {buckets.map((bucket) => {
          const pct = total ? (bucket.value / total) * 100 : 0;
          return (
            <div key={bucket.key}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="h-2.5 w-2.5 flex-none rounded-full" style={{backgroundColor: bucket.color}} />
                  <span className="truncate text-sm font-semibold text-slate-700">{bucket.label}</span>
                </div>
                <span className="text-sm font-bold tabular-nums text-slate-950">{bucket.value} <small className="font-medium text-slate-400">({pct.toFixed(0)}%)</small></span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full" style={{width: `${(bucket.value / maxValue) * 100}%`, backgroundColor: bucket.color}} />
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">{bucket.description}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SupplierRiskPanel({suppliers}: {suppliers: DashboardAnalytics['supplierInsights']}) {
  return (
    <section className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHelp content="Ordena fornecedores pela proporção de produtos em ruptura, risco ou próximos do mínimo dentro do filtro atual." className="absolute right-3 top-3" />
      <div className="flex items-start justify-between gap-3 pr-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-600">Fornecedores</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">Maior concentração de risco</h3>
        </div>
        <span className="rounded-xl bg-orange-50 p-2.5 text-orange-600"><Truck className="h-5 w-5" /></span>
      </div>
      <div className="mt-5 space-y-4">
        {suppliers.slice(0, 5).map((supplier, index) => (
          <div key={supplier.name} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
            <div className="flex items-start gap-3">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white text-xs font-black text-slate-500 shadow-sm">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-bold text-slate-800" title={supplier.name}>{supplier.name}</p>
                  <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-bold ${supplier.alerts ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{supplier.alerts} alertas</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div className={`h-full rounded-full ${supplier.alertPct >= 50 ? 'bg-red-500' : supplier.alertPct >= 25 ? 'bg-orange-500' : 'bg-emerald-500'}`} style={{width: `${Math.max(2, supplier.alertPct)}%`}} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{supplier.products} produtos</span>
                  <span>{supplier.avgCoverage.toFixed(1)}d cobertura</span>
                  <span>{supplier.suggested.toLocaleString('pt-BR')} un sugeridas</span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {!suppliers.length && <p className="py-10 text-center text-sm text-slate-400">Nenhum fornecedor encontrado no filtro atual.</p>}
      </div>
    </section>
  );
}
function CnpjComparison({rows, onSelect}: {rows: CnpjPerformance[]; onSelect: (cnpj: string) => void}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 pr-12">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Comparativo por estabelecimento</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">Desempenho dos CNPJs</h3>
          <p className="mt-1 text-xs text-slate-500">Ordenado pela maior proporção de produtos em alerta</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
          <Building2 className="h-3.5 w-3.5" /> {rows.length} estabelecimentos
        </span>
      </div>
      <div className="hidden grid-cols-[minmax(220px,1.5fr)_100px_minmax(180px,1fr)_120px_130px_100px_24px] gap-4 bg-slate-50 px-6 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400 md:grid">
        <span>CNPJ</span><span className="text-right">Produtos</span><span>Alertas</span><span className="text-right">Cobertura</span><span className="text-right">Pedido sugerido</span><span className="text-right">Índice</span><span />
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((row) => {
          const scoreClasses = row.score >= 75 ? 'bg-emerald-50 text-emerald-700' : row.score >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700';
          return (
            <button
              type="button"
              key={row.cnpj}
              onClick={() => onSelect(row.cnpj)}
              className="group grid w-full gap-3 px-6 py-4 text-left transition-colors hover:bg-teal-50/50 md:grid-cols-[minmax(220px,1.5fr)_100px_minmax(180px,1fr)_120px_130px_100px_24px] md:items-center md:gap-4"
            >
              <span className="min-w-0">
                <strong className="block truncate font-mono text-sm text-slate-900">{formatCNPJ(row.cnpj)}</strong>
                <small className="mt-0.5 block truncate text-xs text-slate-400">{row.razaoSocial || 'Razão social não informada'}</small>
              </span>
              <span className="hidden text-right text-sm font-semibold tabular-nums text-slate-700 md:block">{row.products}</span>
              <span>
                <span className="flex items-center justify-between text-xs"><strong className="text-red-600">{row.alerts} alertas</strong><small className="text-slate-400">{row.alertPct.toFixed(0)}%</small></span>
                <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full bg-gradient-to-r from-orange-400 to-red-500" style={{width: `${Math.max(2, row.alertPct)}%`}} /></span>
              </span>
              <span className="hidden text-right text-sm font-semibold tabular-nums text-slate-700 md:block">{row.avgCoverage.toFixed(1)}d</span>
              <span className="hidden text-right text-sm font-bold tabular-nums text-blue-600 md:block">{row.suggested.toLocaleString('pt-BR')} un</span>
              <span className={`hidden rounded-full px-2.5 py-1 text-center text-xs font-bold md:block ${scoreClasses}`}>{row.score}/100</span>
              <ArrowRight className="hidden h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-teal-600 md:block" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
